import { createSectionTitle, createSummaryTitle, parseHtmlContent } from '../utils';

export const renderOrderAndResult = (data, title) => {
  const content = [createSectionTitle(title)];

  // Handle noDetails case
  if (data && data.noDetails) {
    content.push({
      text: 'No details',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  // Helper function to check if order data is meaningful
  const hasValidOrderData = (data) => {
    if (!data || typeof data !== 'object') return false;

    const hasCptCodes =
      data.cptCodes &&
      Array.isArray(data.cptCodes) &&
      data.cptCodes.some(
        (cpt) => (cpt.value && cpt.value.trim() !== '') || (cpt.label && cpt.label.trim() !== '')
      );

    const hasModifiers =
      data.modifiers &&
      Object.keys(data.modifiers).length > 0 &&
      Object.values(data.modifiers).some(
        (modifierArray) =>
          Array.isArray(modifierArray) &&
          modifierArray.some(
            (modifier) =>
              (modifier.value && modifier.value.trim() !== '') ||
              (modifier.label && modifier.label.trim() !== '')
          )
      );

    const hasIcdCodes =
      data.icdCodes &&
      Array.isArray(data.icdCodes) &&
      data.icdCodes.some(
        (icd) => (icd.value && icd.value.trim() !== '') || (icd.label && icd.label.trim() !== '')
      );

    const hasNote = data.note && data.note.trim() !== '';
    const hasNarrative = data.narrative && data.narrative.trim() !== '';

    return hasCptCodes || hasModifiers || hasIcdCodes || hasNote || hasNarrative;
  };

  // Check if there's any meaningful data to display
  if (!hasValidOrderData(data)) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
    return content;
  }

  if (data.cptCodes && Array.isArray(data.cptCodes) && data.cptCodes.length > 0) {
    // Filter out empty CPT codes
    const validCptCodes = data.cptCodes.filter(
      (cpt) => (cpt.value && cpt.value.trim() !== '') || (cpt.label && cpt.label.trim() !== '')
    );

    if (validCptCodes.length > 0) {
      content.push({
        text: 'CPT CODES',
        style: 'summaryHeader',
        margin: [0, 10, 0, 3]
      });

      const cptRows = [
        [
          { text: 'Code', style: 'tableHeader' },
          { text: 'Description', style: 'tableHeader' }
        ],
        ...validCptCodes.map((cpt) => [
          { text: cpt.value || '', style: 'tableCell' },
          { text: cpt.label || '', style: 'tableCell' }
        ])
      ];

      content.push({
        table: {
          headerRows: 1,
          widths: ['20%', '80%'],
          body: cptRows
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 10,
          paddingTop: () => 4,
          paddingBottom: () => 4
        },
        margin: [0, 6, 0, 0]
      });
    }
  }

  if (data.modifiers && Object.keys(data.modifiers).length > 0) {
    const modifierRows = [
      [
        { text: 'Modifier', style: 'tableHeader' },
        { text: 'Description', style: 'tableHeader' }
      ]
    ];

    Object.keys(data.modifiers).forEach((key) => {
      if (Array.isArray(data.modifiers[key])) {
        data.modifiers[key].forEach((modifier) => {
          // Only add modifiers with meaningful data
          if (
            (modifier.value && modifier.value.trim() !== '') ||
            (modifier.label && modifier.label.trim() !== '')
          ) {
            modifierRows.push([
              { text: modifier.value || '', style: 'tableCell' },
              { text: modifier.label || '', style: 'tableCell' }
            ]);
          }
        });
      }
    });

    if (modifierRows.length > 1) {
      content.push({
        text: 'MODIFIERS',
        style: 'summaryHeader',
        margin: [0, 10, 0, 3]
      });

      content.push({
        table: {
          headerRows: 1,
          widths: ['20%', '80%'],
          body: modifierRows
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 10,
          paddingTop: () => 4,
          paddingBottom: () => 4
        },
        margin: [0, 6, 0, 0]
      });
    }
  }

  if (data.icdCodes && Array.isArray(data.icdCodes) && data.icdCodes.length > 0) {
    // Filter out empty ICD codes
    const validIcdCodes = data.icdCodes.filter(
      (icd) => (icd.value && icd.value.trim() !== '') || (icd.label && icd.label.trim() !== '')
    );

    if (validIcdCodes.length > 0) {
      content.push({
        text: 'ICD CODES',
        style: 'summaryHeader',
        margin: [0, 10, 0, 3]
      });

      const icdRows = [
        [
          { text: 'Code', style: 'tableHeader' },
          { text: 'Description', style: 'tableHeader' }
        ],
        ...validIcdCodes.map((icd) => [
          { text: icd.value || '', style: 'tableCell' },
          { text: icd.label || '', style: 'tableCell' }
        ])
      ];

      content.push({
        table: {
          headerRows: 1,
          widths: ['20%', '80%'],
          body: icdRows
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 10,
          paddingTop: () => 4,
          paddingBottom: () => 4
        },
        margin: [0, 6, 0, 0]
      });
    }
  }

  if (data.note && data.note.trim() !== '') {
    content.push({
      text: 'NOTES',
      style: 'summaryHeader',
      margin: [0, 10, 0, 3]
    });
    content.push({
      text: parseHtmlContent(data.note),
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  // Show narrative if it exists
  if (data.narrative && data.narrative.trim() !== '') {
    content.push(createSummaryTitle());
    content.push({
      text: parseHtmlContent(data.narrative),
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  // If no content was added beyond title, show "No data"
  if (content.length === 1) {
    content.push({
      text: 'No data',
      style: 'narrative',
      margin: [0, 0, 0, 8]
    });
  }

  return content;
};
